import React from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function CalculusOfEndurance() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Allostatic Load &middot; Basal Ganglia &middot; The Automation of Survival</span>
          <h1>The Calculus of Endurance</h1>
          <p className="calculus-hero-sub">Resilience is choosing to get back up. Endurance is when getting back up is no longer a choice. It is what your body does. The alarm goes off. The scrubs go on. There is no decision anymore. There is only the next shift.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* The Bathroom */}
          <section className="calc-section reveal">
            <h2>The Bathroom</h2>
            <p className="calc-lead">
              She is crying in the bathroom. The one with the lock that sticks. The one
              between the supply closet and the linen room that nobody uses because the
              light flickers. It is the only room in the building where a CNA can be
              alone for two minutes.
            </p>
            <p>
              She does not know why she is crying. There was no single event. No patient
              screamed at her. No nurse dismissed her. Nothing happened that has not
              happened a hundred times before. That is the point. Nothing happened. And
              the tears came anyway. Because the system that processes sadness has been
              running at capacity for so long that it no longer requires a trigger. The
              tears are not a response. They are an overflow. A pressure valve opening
              because the pressure never stops.
            </p>
            <p>
              She washes her face. She dries her hands on the paper towels that are always
              almost out. She opens the door.
            </p>
            <p>
              She walks back onto the floor.
            </p>
            <p>
              Not because she chose to. Not because she weighed the costs and benefits and
              decided the patients needed her more than she needed to sit in the bathroom
              and cry. She walks back out because that is what she does. The way breathing
              is what she does. The way blinking is what she does. Walking back out has
              stopped being a decision. It has become architecture.
            </p>
            <p>
              That is not resilience. Resilience is the chapter before this one. Resilience
              is the conscious choice, the dopamine of "I can," the aMCC growing from hard
              things, the feedback loop that strengthens with use.
            </p>
            <p>
              This is what happens after the loop has run so many times it automated.
              This is endurance. And endurance has a different chemistry entirely.
            </p>
          </section>

          <Pulse />

          {/* The Transfer */}
          <section className="calc-section reveal">
            <h2>The Transfer</h2>
            <p>
              In the early days, every hard thing was a decision. The choice to go back
              to calculus after failing it. The choice to walk into a classroom five days
              after giving birth. The choice to nurse a baby during a lecture while the
              professor held her during pop quizzes. Those were resilience. The prefrontal
              cortex was involved. The cost-benefit computation ran every time. And every
              time, the dopamine of purpose outweighed the cortisol of exhaustion.
            </p>
            <p>
              But the brain does not keep computing things it has already decided. That
              would be metabolically wasteful. Evolution solved this with a mechanism
              called automaticity: when a behavior is repeated enough times, control
              transfers from the prefrontal cortex to the basal ganglia. The behavior
              becomes procedural. Habitual. Automatic.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th>Brain Region</th>
                    <th>Experience</th>
                    <th>Chemistry</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td><strong>Decision</strong></td><td>Prefrontal cortex</td><td>"Should I go back?"</td><td>Dopamine vs cortisol. Active computation. Feels hard.</td></tr>
                  <tr><td><strong>Resilience</strong></td><td>Anterior mid-cingulate</td><td>"I choose to go back."</td><td>aMCC growth. Feedback loop. Feels like willpower.</td></tr>
                  <tr><td><strong>Endurance</strong></td><td>Basal ganglia</td><td>"I am going back."</td><td>Automated. Procedural. Feels like nothing. That is the problem.</td></tr>
                </tbody>
              </table>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Automaticity Transfer</span>
              <code>if repetitions &gt; &theta;_habit &rarr; control transfers: prefrontal cortex &rarr; basal ganglia</code>
              <span className="calc-eq-desc">When a behavior has been repeated past the habit threshold, the brain transfers control from conscious decision-making (prefrontal cortex) to automatic execution (basal ganglia). The person no longer decides to persist. They persist the way they breathe. The CNA who walks out of the bathroom is not choosing. Her basal ganglia is executing a procedure that her prefrontal cortex wrote years ago and has since stopped monitoring.</span>
            </div>

            <p>
              This is the part nobody talks about. Resilience is celebrated. Motivational
              speakers build careers on it. "Choose to get back up." "Decide to be strong."
              "Your mindset determines your outcome."
            </p>
            <p>
              But what happens when the getting back up is no longer a mindset? When it
              has transferred to the basal ganglia and the person is not deciding anymore?
              When the alarm goes off at five and the scrubs go on and the feet hit the
              floor and there is no internal monologue, no dopamine computation, no
              cost-benefit analysis? Just the next shift. Just the routine. Just the body
              doing what it was trained to do by ten thousand prior repetitions.
            </p>
            <p>
              That is endurance. And it is invisible. Because it does not look like struggle.
              It looks like someone who is fine.
            </p>
          </section>

          <Pulse />

          {/* Allostatic Load */}
          <section className="calc-section reveal">
            <h2>The Price of Adaptation</h2>
            <p>
              Bruce McEwen coined the term "allostatic load" in 1993 to describe the
              cumulative wear and tear on the body from chronic stress adaptation. The
              concept is precise: the body adapts to sustained stress by recalibrating
              its baseline. Cortisol stops spiking because cortisol IS the baseline now.
              The system is not stressed. The system has redefined stress as normal.
            </p>
            <p>
              This is allostasis: stability through change. The body's ability to maintain
              function by adjusting its set points. In the short term, it is brilliant. The
              cortisol that would have been debilitating as an acute spike becomes
              manageable as a chronic plateau. The CNA stops feeling the stress because her
              body has adapted to it as the default operating condition.
            </p>
            <p>
              But allostasis has a cost. The adaptation itself consumes resources. Chronic
              cortisol elevation, even at the adapted plateau, suppresses hippocampal
              neurogenesis. The hippocampus shrinks. Memory consolidation suffers. The
              prefrontal cortex, the region that made the original resilience decisions,
              thins under sustained glucocorticoid exposure. The amygdala, meanwhile,
              grows. Threat detection becomes more sensitive as executive function declines.
            </p>

            <div className="calc-equation">
              <span className="calc-eq-label">Allostatic Load</span>
              <code>AL(t) = &int;<sub>0</sub><sup>T</sup> [cortisol_baseline(t) &minus; cortisol_healthy] &middot; duration(t) dt</code>
              <span className="calc-eq-desc">Allostatic load is the integral over time of the difference between the adapted cortisol baseline and the healthy cortisol baseline, multiplied by duration. It accumulates invisibly. The person feels fine because the adaptation is working. The tissue damage accumulates because the adaptation has a metabolic cost. By the time the person feels not fine, the load has been accruing for years.</span>
            </div>

            <blockquote>
              Endurance is resilience that has become habit. And the habit is quietly
              consuming the brain regions that made the original choice possible. She is
              persisting because her basal ganglia automated the persistence. But the
              prefrontal cortex that wrote the program is thinning under the cortisol
              that the persistence requires her to absorb. The machine is running. The
              machine is also wearing out. And nobody is measuring the wear because the
              machine looks fine from the outside.
            </blockquote>
          </section>

          <Pulse />

          {/* The Invisible Erosion */}
          <section className="calc-section reveal">
            <h2>The Invisible Erosion</h2>
            <p>
              She does not know her hippocampus is shrinking. She does not know her
              prefrontal cortex is thinning. She knows she forgot where she put her keys.
              She knows she cannot remember if she charted Room 8 or Room 9. She knows
              her patience is shorter than it used to be, that the fuse between
              "fine" and "snapping" has gotten smaller, that she used to be able to absorb
              a resident's anger without flinching and now she has to breathe through it
              deliberately.
            </p>
            <p>
              She attributes this to aging. To tiredness. To the normal wear of a hard
              job. She does not know it is measurable. She does not know it has a name.
              She does not know that the system she works in is consuming the organ she
              thinks with, slowly, shift by shift, year by year, and that the adaptation
              that allows her to endure is the mechanism by which the endurance erodes her.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Brain Region</th>
                    <th>Function</th>
                    <th>Effect of Chronic Cortisol</th>
                    <th>What She Notices</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Hippocampus</td><td>Memory consolidation</td><td>Volume decreases. Neurogenesis suppressed.</td><td>"I keep forgetting things."</td></tr>
                  <tr><td>Prefrontal cortex</td><td>Decision-making, impulse control</td><td>Cortical thinning. Executive function declines.</td><td>"My patience is gone."</td></tr>
                  <tr><td>Amygdala</td><td>Threat detection</td><td>Volume increases. Sensitivity heightens.</td><td>"Everything feels like a crisis."</td></tr>
                  <tr><td>Anterior cingulate</td><td>Persistence computation</td><td>Connectivity weakened by sustained load.</td><td>"I don't care anymore."</td></tr>
                </tbody>
              </table>
            </div>

            <p>
              "I don't care anymore" is not burnout. It is the anterior cingulate cortex,
              the aMCC, the same region that grew from every act of willpower, losing its
              connectivity under sustained allostatic load. The region that computes
              "should I keep going" has been degraded by the very process of keeping going.
            </p>
            <p>
              This is the cruelty of endurance without support. The mechanism that was built
              by choosing hard things is eroded by the sustained cost of hard things never
              stopping. The aMCC grew because she persisted. The aMCC weakens because the
              persistence never ends. Growth requires recovery. Endurance without recovery
              is consumption.
            </p>
          </section>

          <Pulse />

          {/* What an AI Could See */}
          <section className="calc-section reveal">
            <h2>What an AI Could See</h2>
            <p>
              The difference between resilience and endurance is invisible to every
              monitoring system in healthcare. Both look the same from outside: the CNA
              shows up, does her job, goes home. The system sees attendance. It does not
              see the transfer from prefrontal cortex to basal ganglia. It does not see
              the allostatic load accumulating. It does not see the hippocampus shrinking
              or the amygdala growing or the aMCC losing connectivity.
            </p>
            <p>
              It sees a reliable employee. Until the day she quits. And then it sees a
              staffing problem.
            </p>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>At year one: the system measures resilience.</h4>
                <p>The CNA is choosing to come back. Her aMCC is growing. Her dopamine
                pathways are strengthening. She is in the resilience phase. The feedback
                loop is active. The cost-benefit computation runs every morning and
                dopamine wins. She is building the architecture of endurance.</p>
              </div>
              <div className="calc-implication">
                <h4>At year three: the system detects the transfer.</h4>
                <p>The CNA no longer chooses. She arrives automatically. The basal ganglia
                has taken over. The resilience feedback loop has automated. She no longer
                needs dopamine to come to work. She comes to work the way she breathes.
                The system should flag this: automaticity without recovery support means
                allostatic load is accumulating without the conscious awareness that would
                trigger self-care.</p>
              </div>
              <div className="calc-implication">
                <h4>At year five: the system measures allostatic load.</h4>
                <p>Response latency has increased. Decision quality has decreased. Emotional
                reactivity has increased. The pattern is not random. It follows the
                trajectory of chronic cortisol exposure on prefrontal and hippocampal
                tissue. The system does not wait for a critical incident. It intervenes
                with mandatory recovery time, workload reduction, or role rotation.
                Not as punishment. As pharmacology.</p>
              </div>
              <div className="calc-implication">
                <h4>Before the quit: the system sees the erosion the person cannot.</h4>
                <p>"I don't care anymore" is measurable before the person says it. The
                shift from purposeful engagement to automated endurance has a
                neurochemical signature: dopamine flattens, cortisol plateaus, serotonin
                baselines drop. The CNA who is about to quit has been showing the
                chemistry of quitting for months. The system was not looking. An AI that
                models endurance as distinct from resilience would have seen it.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* Recovery */}
          <section className="calc-section reveal">
            <h2>The One Variable</h2>
            <p>
              There is one variable that separates endurance that sustains from endurance
              that consumes. It is not willpower. It is not motivation. It is not mindset
              or grit or any other word the motivational industry sells to people who are
              already past the point of choosing.
            </p>
            <p>
              It is recovery.
            </p>
            <p>
              The aMCC grows from hard things, but only if hard things are followed by rest.
              Muscle fibers grow from resistance training, but the growth happens during rest,
              not during the lift. Dopamine pathways strengthen through use, but the
              strengthening requires downtime for consolidation. Every neuroplastic change
              that resilience produces requires a period of low cortisol, elevated serotonin,
              parasympathetic activation for the change to consolidate.
            </p>
            <p>
              Endurance without recovery is a muscle that is never allowed to rest between
              sets. It does not grow. It tears. The fibers do not consolidate. They fray.
              The CNA who works three twelve-hour shifts back to back with no recovery day
              is not building endurance. She is tearing the tissue of her persistence and
              calling it strength because no one told her the difference.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Endurance Equation</span>
              <code>E(t) = R(t) &minus; AL(t) + &sum; recovery_events &middot; consolidation_rate</code>
              <span className="calc-eq-desc">Endurance at any point in time is resilience minus accumulated allostatic load plus the sum of recovery events weighted by consolidation rate. When recovery is zero, endurance equals resilience minus damage. The trajectory is always downward. When recovery is present, the consolidation term offsets the load. The trajectory can be sustained indefinitely. The only variable the system controls is recovery. Everything else is the person's biology doing its best.</span>
            </div>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>She cried in the bathroom.</em></p>
            <p className="calc-closing-line"><em>She washed her face.</em></p>
            <p className="calc-closing-line"><em>She walked back out.</em></p>
            <p className="calc-closing-line"><em>Not because she chose to. Because her body forgot how not to.</em></p>
            <p className="calc-closing-final">That is the calculus of endurance.<br />It was always chemistry.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CalculusOfEndurance;
